'use strict'

// Account AI token allotment for one billing period.
// The default is 1,000,000 tokens. A named plan in PLAN_ALLOTMENTS, or
// billing.tokenAllotment on the account, raises that number.
// With no periodAnchor the period is the UTC calendar month. periodAnchor
// is the subscription start, and each period runs one month from that date.

const DEFAULT_AI_TOKEN_ALLOTMENT = 1000000

const PLAN_ALLOTMENTS = {
  // studio: 5000000
}

function positiveInt (value) {

  const number = Number(value)

  if (!Number.isFinite(number) || number <= 0) return null

  return Math.floor(number)
}

function allotmentForBilling (billing, plans) {

  const table = plans || PLAN_ALLOTMENTS
  const explicit = positiveInt(billing && billing.tokenAllotment)

  if (explicit) return explicit

  const plan = billing && typeof billing.plan === 'string' ? billing.plan.trim() : ''
  const named = positiveInt(plan && table[plan])

  if (named) return named

  return DEFAULT_AI_TOKEN_ALLOTMENT
}

function addUtcMonths (date, count) {

  const year = date.getUTCFullYear()
  const monthIndex = date.getUTCMonth() + count
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()

  return new Date(Date.UTC(
    year,
    monthIndex,
    Math.min(date.getUTCDate(), lastDay),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  ))
}

function calendarPeriod (now) {

  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  const month = String(now.getUTCMonth() + 1).padStart(2, '0')

  return {
    key: `${now.getUTCFullYear()}-${month}`,
    start: start.toISOString(),
    end: end.toISOString()
  }
}

function anchoredPeriod (anchor, now) {

  let months = (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 + (now.getUTCMonth() - anchor.getUTCMonth())

  if (months < 0) return calendarPeriod(now)

  let start = addUtcMonths(anchor, months)

  if (start.getTime() > now.getTime()) {

    months -= 1

    if (months < 0) return calendarPeriod(now)

    start = addUtcMonths(anchor, months)
  }

  const end = addUtcMonths(anchor, months + 1)

  return {
    key: start.toISOString().slice(0, 10),
    start: start.toISOString(),
    end: end.toISOString()
  }
}

function billingPeriod (date, billing) {

  const parsed = date instanceof Date ? date : new Date(date)
  const now = Number.isNaN(parsed.getTime()) ? new Date() : parsed
  const anchor = billing && billing.periodAnchor ? new Date(billing.periodAnchor) : null

  if (!anchor || Number.isNaN(anchor.getTime()) || anchor.getTime() > now.getTime()) {

    return calendarPeriod(now)
  }

  return anchoredPeriod(anchor, now)
}

function groupDigits (value) {

  const whole = Math.max(0, Math.floor(Number(value) || 0))

  return String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function presentTokenUsage (counts, allowance) {

  const inputTokens = Math.max(0, Math.floor(Number(counts && counts.inputTokens) || 0))
  const outputTokens = Math.max(0, Math.floor(Number(counts && counts.outputTokens) || 0))
  const allotment = positiveInt(allowance && allowance.allotment) || DEFAULT_AI_TOKEN_ALLOTMENT

  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    allotment,
    periodKey: allowance && allowance.periodKey ? String(allowance.periodKey) : '',
    periodStart: allowance && allowance.periodStart ? String(allowance.periodStart) : '',
    periodEnd: allowance && allowance.periodEnd ? String(allowance.periodEnd) : ''
  }
}

function tokenUsageLabel (usage) {

  const total = usage && usage.totalTokens
  const allotment = usage && usage.allotment != null ? usage.allotment : DEFAULT_AI_TOKEN_ALLOTMENT

  return `${groupDigits(total)} / ${groupDigits(allotment)} tokens this period`
}

function tokenUsageDetail (usage) {

  if (!usage) return ''

  return `${groupDigits(usage.inputTokens)} input and ${groupDigits(usage.outputTokens)} output`
}

module.exports = {
  DEFAULT_AI_TOKEN_ALLOTMENT,
  PLAN_ALLOTMENTS,
  allotmentForBilling,
  billingPeriod,
  presentTokenUsage,
  tokenUsageLabel,
  tokenUsageDetail
}
